import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Shield, Users, Landmark, Coins, FileText, Calendar, Calculator, BookOpen, PenLine, MessageCircle, ClipboardList } from "lucide-react";
import { WhatsAppPanel } from "@/components/WhatsAppPanel";
import { SignaturesPanel } from "@/components/Signatures";
import { ActivitiesPanel } from "@/components/activities/ActivitiesPanel";
import { api, fmtDate } from "@/lib/api";
import { ENTITY_DEFS } from "@/lib/entities";
import { useAuth } from "@/context/AuthContext";
import { EntityManager } from "@/components/EntityManager";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { ReportButton } from "@/components/ReportButton";
import { Card, StatusBadge } from "@/components/Ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TABS = [
  ["config", "Configurazione", Shield, "trusts"], ["subjects", "Soggetti", Users, "trust_subjects"], ["assets", "Patrimonio", Landmark, "assets"],
  ["accounting", "Contabilità", Coins, "accounting"], ["documents", "Documenti", FileText, "documents"], ["activities", "Attività", ClipboardList, "trusts"], ["deadlines", "Scadenze", Calendar, "deadlines"],
  ["fiscal", "Fiscale", Calculator, "fiscal"], ["diary", "Diario", BookOpen, "diary"], ["signatures", "Firme", PenLine, "documents"],
  ["whatsapp", "WhatsApp", MessageCircle, "trust_subjects"],
];

function Config({ trust }) {
  const fields = ENTITY_DEFS.trusts.fields;
  const show = (f) => {
    const v = trust[f.k];
    if (f.t === "date") return fmtDate(v);
    if (f.t === "select") return f.o.find((o) => o.value === v)?.label || v || "—";
    return v || "—";
  };
  return (
    <Card className="p-6">
      <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
        {fields.map((f) => (
          <div key={f.k} className={f.t === "textarea" ? "sm:col-span-2 lg:col-span-3" : ""}>
            <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.l}</dt>
            <dd className="text-sm text-slate-900 mt-1 whitespace-pre-wrap" data-testid={`trust-config-${f.k}`}>{show(f)}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

export default function TrustDetail() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const { can, hasRole } = useAuth();
  const [trust, setTrust] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/e/trusts/${id}`).then((r) => setTrust(r.data)).catch(() => setErr("Trust non trovato o non collegato alla tua utenza")); }, [id]);

  if (err) return <p className="text-red-600" data-testid="trust-detail-error">{err}</p>;
  if (!trust) return <p className="text-slate-400">Caricamento…</p>;
  const tabs = TABS.filter(([v, , , sec]) => (v === "whatsapp" ? hasRole("admin", "gestore_utenti", "collaboratore") : sec === "trusts" || can(sec)));

  return (
    <div className="tm-fade">
      <Link to="/trusts" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="trust-back-link"><ArrowLeft className="h-4 w-4" />Progetti Trust</Link>
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="trust-detail-name">{trust.name}</h1>
        <StatusBadge value={trust.status} />
        {trust.code && <span className="font-mono text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">{trust.code}</span>}
        {can("accounting") && <div className="ml-auto"><ReportButton trust={trust} /></div>}
      </div>
      <Tabs key={search.get("tab") || "config"} defaultValue={search.get("tab") || "config"}>
        <TabsList className="bg-white border border-slate-200 h-auto flex-wrap justify-start p-1 mb-6">
          {tabs.map(([v, l, I]) => (
            <TabsTrigger key={v} value={v} data-testid={`trust-tab-${v}`} className="gap-1.5 text-xs data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"><I className="h-3.5 w-3.5" />{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="config"><Config trust={trust} /></TabsContent>
        {tabs.filter(([v]) => v !== "config").map(([v, , , sec]) => (
          <TabsContent key={v} value={v}>
            {v === "whatsapp" ? <WhatsAppPanel trust={trust} /> : v === "activities" ? <ActivitiesPanel trustId={id} /> : v === "signatures" ? <SignaturesPanel trustId={id} /> : sec === "documents" ? <DocumentsPanel trustId={id} /> : <EntityManager entity={sec} trustId={id} compact />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
